"use client";

import { FC, useState } from "react";
import {
  Stack,
  Field,
  Input,
  Alert,
  Text,
  Link,
  Flex,
  List,
} from "@chakra-ui/react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { signIn } from "next-auth/react";
import NextLink from "next/link";
import { useQuery } from "@apollo/client/react";
import gql from "graphql-tag";
import { CheckIcon } from "lucide-react";
import CenteredPageLayout from "../../../components/PageLayout/CenteredPageLayout";
import Button from "../../../components/Button";
import { MIN_PASSWORD_LENGTH, MAX_PASSWORD_LENGTH } from "../../../constants";
import { Price } from "../../../types/subscription";
// import { ApiResponse } from "../../../../types/api";
// import { ErrorCode } from "../../../../lib/error";

const GET_PRICE = gql`
  query GetPrice {
    price {
      id
      value
    }
  }
`;

type Inputs = {
  email: string;
  password: string;
};

const RegisterPage: FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const { data: priceData } = useQuery<{ price: Price }>(GET_PRICE);

  const price = priceData?.price.value;
  const [amount, term] = price?.substring(1).split("/") ?? [];

  const { control, handleSubmit, setError } = useForm<Inputs>({
    defaultValues: {},
  });

  const onSubmit: SubmitHandler<Inputs> = async ({ email, password }) => {
    setIsLoading(true);

    // register account in pending state
    const registerResponse = await fetch("/api/account/register", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });

    const registerData = await registerResponse.json();

    if (!registerData.success) {
      const { code, error: message } = registerData;

      if (code === "EMAIL_TAKEN") {
        setError("email", { message });
      } else if (code === "INVALID_PASSWORD") {
        setError("password", { message });
      } else {
        setFormError("Something went wrong");
      }

      setIsLoading(false);
    } else {
      await signIn("credentials", {
        redirect: true,
        callbackUrl: "/dashboard",
        email,
        password,
      });
    }
  };

  return (
    <CenteredPageLayout
      title="Register"
      links={[
        { label: "Back to login", url: "/login" },
        { label: "Refund policy", url: "/terms-and-conditions#refund-policy" },
      ]}
    >
      <Stack gap={4}>
        {formError && (
          <Alert.Root status="error">
            <Alert.Indicator />
            <Alert.Content>
              <Alert.Description>{formError}</Alert.Description>
            </Alert.Content>
          </Alert.Root>
        )}

        <form onSubmit={handleSubmit(onSubmit)}>
          {price && (
            <Flex justifyContent="center" mb={4}>
              <Text
                as="span"
                fontWeight="bold"
                fontSize={26}
                textTransform="uppercase"
                lineHeight={1}
                position="relative"
                top="13px"
              >
                £
              </Text>
              <Text
                as="span"
                fontWeight={400}
                fontSize={60}
                textTransform="uppercase"
                lineHeight={1}
              >
                {amount}
              </Text>
              <Text
                as="span"
                fontSize={18}
                fontWeight="bold"
                lineHeight={1}
                position="relative"
                top="36px"
                ml="5px"
                color="#9d9d9d"
              >
                /{term}
              </Text>
            </Flex>
          )}

          <Text
            as="h2"
            fontWeight="bold"
            fontSize={20}
            color="#ec7c74"
            textTransform="uppercase"
            textAlign="center"
          >
            Zero to Finals Members
          </Text>

          <List.Root mt={5} mb={7} gap={1}>
            {[
              "How to Learn Medicine Course",
              "Digital Flashcards",
              "Fact Trainer Tool",
              "Short Answer Questions",
              "Multiple Choice Questions",
              "Extended Matching Questions",
              "Results Review Page",
              "Revision Tracking Tool",
              "OSCE Practice Tool",
            ].map(text => (
              <List.Item key={text}>
                <Flex gap={2}>
                  <CheckIcon color="#023648" />
                  <Text>{text}</Text>
                </Flex>
              </List.Item>
            ))}
          </List.Root>

          <Stack gap={4}>
            <Controller
              control={control}
              name="email"
              defaultValue=""
              rules={{
                required: "Field required",
              }}
              render={({ field, fieldState: { error } }) => (
                <Field.Root invalid={!!error}>
                  <Field.Label>Email</Field.Label>
                  <Input {...field} type="email" />
                  {error && <Field.ErrorText>{error.message}</Field.ErrorText>}
                </Field.Root>
              )}
            />

            <Controller
              control={control}
              name="password"
              defaultValue=""
              rules={{
                required: "Field required",
                minLength: {
                  value: MIN_PASSWORD_LENGTH,
                  message: `Password must be between ${MIN_PASSWORD_LENGTH} and ${MAX_PASSWORD_LENGTH} characters`,
                },
                maxLength: {
                  value: MAX_PASSWORD_LENGTH,
                  message: `Password must be between ${MIN_PASSWORD_LENGTH} and ${MAX_PASSWORD_LENGTH} characters`,
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <Field.Root invalid={!!error}>
                  <Field.Label>Password</Field.Label>
                  <Input {...field} type="password" />
                  {error && <Field.ErrorText>{error.message}</Field.ErrorText>}
                </Field.Root>
              )}
            />

            <Text fontSize={14} mb={3}>
              By creating an account, you acknowledge and agree to our{" "}
              <Link
                asChild
                variant="underline"
                color="primary"
                fontWeight={600}
              >
                <NextLink href="/terms-and-conditions" target="_blank">
                  terms and conditions
                </NextLink>
              </Link>
              .
            </Text>

            <div>
              <Button visual="primary" type="submit" loading={isLoading}>
                Register
              </Button>
            </div>
          </Stack>
        </form>
      </Stack>
    </CenteredPageLayout>
  );
};

export default RegisterPage;
